import { useEffect, useRef } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import GlobalStyles from "@mui/material/GlobalStyles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { CALIBRATION_FIELDS, OPERATION_KIND_LABEL, OPERATION_STATUS_LABEL, setupState } from "./operationsPlan";
import { ACCESS_LABEL } from "./ContactAccessTable";

const PRINT_CLASS = "rivio-installation-summary-print";

const printStyles = (
  <GlobalStyles
    styles={{
      "@media print": {
        "@page": { margin: "14mm" },
        "body *": { visibility: "hidden" },
        [`.${PRINT_CLASS}, .${PRINT_CLASS} *`]: { visibility: "visible" },
        [`.${PRINT_CLASS}`]: {
          position: "absolute",
          left: 0,
          top: 0,
          width: "100%",
          color: "#000 !important",
          background: "#fff !important",
        },
        [`.${PRINT_CLASS} .no-print`]: { display: "none !important" },
        [`.${PRINT_CLASS} table`]: { pageBreakInside: "auto", width: "100%" },
        [`.${PRINT_CLASS} tr`]: { pageBreakInside: "avoid" },
        [`.${PRINT_CLASS} h5, .${PRINT_CLASS} h6`]: { pageBreakAfter: "avoid" },
        [`.${PRINT_CLASS} section`]: { pageBreakInside: "avoid" },
      },
    }}
  />
);

function fmtDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString() : String(value);
}

function Section({ title, children }) {
  return (
    <Box component="section" mb={3}>
      <Typography variant="h6" fontWeight={700} mb={1}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function KV({ rows }) {
  return (
    <Table size="small">
      <TableBody>
        {rows.map(([label, value]) => (
          <TableRow key={label}>
            <TableCell sx={{ width: "38%", fontWeight: 600, verticalAlign: "top" }}>{label}</TableCell>
            <TableCell sx={{ wordBreak: "break-word" }}>{value == null || value === "" ? "-" : value}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function Grid({ head, rows, empty }) {
  if (!rows.length) return <Typography variant="body2">{empty}</Typography>;
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead sx={{ display: "table-header-group" }}>
          <TableRow>
            {head.map((label) => (
              <TableCell key={label} sx={{ fontWeight: 700 }}>
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((cells, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <TableRow key={index}>
              {cells.map((cell, cellIndex) => (
                // eslint-disable-next-line react/no-array-index-key
                <TableCell key={cellIndex}>{cell == null || cell === "" ? "-" : cell}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function statusText(row) {
  return OPERATION_STATUS_LABEL[row?.status] || row?.status || "Pending";
}

function calibrationValue(field, unit, value) {
  if (value == null || value === "") return "-";
  if (field === "sensor_mount_type") {
    return { top: "Top of tank", bottom: "Bottom-mounted" }[value] || String(value);
  }
  return `${value}${unit ? ` ${unit}` : ""}`;
}

function lastAttempt(row) {
  return (row.attempts || []).map((a) => a.created_at).sort().pop();
}

function contactName(contacts, contactId, fallback) {
  const found = (contacts || []).find((row) => Number(row.contact_id) === Number(contactId));
  return found?.name || fallback || (contactId ? `Contact #${contactId}` : "New contact");
}

/**
 * Renders the server-confirmed installation record (snapshot + follow-up
 * ledger). Never renders from local form state.
 */
export default function InstallationSummary({ summary, operations: liveOperations, onRetry, retrying, error, historical = false }) {
  const previousTitle = useRef(null);
  const snapshot = summary?.snapshot || {};
  const deviceId = snapshot.device_id;

  useEffect(() => {
    const restore = () => {
      if (previousTitle.current != null) {
        document.title = previousTitle.current;
        previousTitle.current = null;
      }
    };
    window.addEventListener("afterprint", restore);
    return () => window.removeEventListener("afterprint", restore);
  }, []);

  if (!summary) return null;
  const operations = liveOperations || summary.operations || [];
  const state = setupState(operations);
  const target = snapshot.target || {};
  const entity = target.entity || {};
  const address = target.address || {};
  const asset = target.asset || {};
  const item = target.item || {};
  const contacts = target.contacts || [];
  const installation = snapshot.installation || {};
  const subscription = snapshot.subscription || {};
  const policy = snapshot.subscription_policy || {};
  const attestation = snapshot.attestation || {};
  const labels = snapshot.labels || {};
  const opsOf = (kind) => operations.filter((row) => row.kind === kind);
  const failed = operations.filter((row) => row.status === "failed" || row.status === "pending" || row.status === "processing");

  const download = () => {
    previousTitle.current = document.title;
    // Browsers use the document title as the suggested PDF filename.
    document.title = `rivio-installation-device-${deviceId ?? "summary"}`;
    window.print();
  };

  return (
    <Box className={PRINT_CLASS}>
      {printStyles}
      {summary.partial ? (
        <Alert severity="info" sx={{ mb: 2, borderRadius: "12px" }}>
          Partial historical record. This installation completed before full summaries were captured, so only the
          recorded claim details are shown. Current customer records are not substituted.
        </Alert>
      ) : state === "needs_attention" ? (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: "12px" }}>
          Installation saved - some setup still needs attention.
        </Alert>
      ) : state === "pending" ? (
        <Alert severity="info" sx={{ mb: 2, borderRadius: "12px" }}>
          Installation saved - finishing setup.
        </Alert>
      ) : (
        <Alert severity="success" sx={{ mb: 2, borderRadius: "12px" }}>
          Installation complete. This claim cannot be reused.
        </Alert>
      )}
      {error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }} className="no-print">
          {error}
        </Alert>
      ) : null}

      <Box display="flex" flexWrap="wrap" gap={1} mb={2} className="no-print">
        <Button variant="contained" onClick={download} data-installation-pdf sx={{ textTransform: "none", fontWeight: 700 }}>
          Download installation summary (PDF)
        </Button>
        {failed.length > 0 && onRetry && (
          <Button variant="outlined" onClick={() => onRetry(failed)} disabled={retrying} sx={{ textTransform: "none", fontWeight: 700 }}>
            {retrying ? <CircularProgress size={18} /> : `Retry ${failed.length} setup item${failed.length > 1 ? "s" : ""}`}
          </Button>
        )}
      </Box>
      <Typography variant="caption" display="block" mb={2} className="no-print">
        The download opens your browser&apos;s print dialog. Choose &quot;Save as PDF&quot; as the destination.
      </Typography>

      <Typography variant="h5" fontWeight={700} mb={0.5}>
        Installation summary - Device #{deviceId ?? "-"}
      </Typography>
      <Typography variant="body2" mb={2}>
        {historical ? "At installation. " : ""}
        {summary.partial
          ? `Claim #${snapshot.claim_id ?? "-"}; completed ${fmtDateTime(snapshot.install_completed_at)}.`
          : `Snapshot v${snapshot.version ?? snapshot.schema_version ?? 1} recorded ${fmtDateTime(snapshot.captured_at)} for claim #${snapshot.claim_id ?? "-"}.`}
      </Typography>

      {summary.partial ? (
        <Section title="Recorded installation">
          <KV
            rows={[
              ["Device", `Device #${snapshot.device_id ?? "-"}`],
              ["Installation date", snapshot.installation_date ? String(snapshot.installation_date).slice(0, 10) : "-"],
              ["Installed customer ID", snapshot.installed_entity_id],
              ["Installed asset ID", snapshot.installed_address_item_id],
              ["Completed", fmtDateTime(snapshot.install_completed_at)],
            ]}
          />
        </Section>
      ) : (
        <>
          <Section title="Customer and site">
            <KV
              rows={[
                ["Customer", `${entity.display_name || `Customer #${entity.entity_id}`} (ID ${entity.entity_id ?? "-"})`],
                ["Entity type", entity.entity_type],
                ["Status", entity.status],
                ["Company", entity.company],
                ["Service address label", address.name],
                ["Service address", address.formatted_address],
                [
                  "Location",
                  address.latitude != null && address.longitude != null ? `${address.latitude}, ${address.longitude}` : "-",
                ],
                ["Tank / asset", `${asset.name || item.name || "-"} (ID ${asset.address_item_id ?? "-"})`],
                ["Asset type", item.name],
                ["Asset identifier", asset.map_identifier],
              ]}
            />
            {(target.attributes || []).length > 0 && (
              <Box mt={1}>
                <Grid
                  head={["Asset detail", "Value"]}
                  rows={(target.attributes || []).map((row) => [
                    row.label || `Attribute ${row.attribute_id}`,
                    row.value,
                  ])}
                  empty=""
                />
              </Box>
            )}
          </Section>

          <Section title="Contacts">
            <Grid
              head={["Contact", "ID", "Type", "Primary", "Phone / email"]}
              rows={contacts.map((row) => [
                row.name,
                row.contact_id,
                row.contact_type,
                row.is_primary ? "Yes" : "",
                (row.addresses || []).map((a) => a.value).join(", "),
              ])}
              empty="No contacts were associated with this customer at installation."
            />
          </Section>

          <Section title="Device and subscription">
            <KV
              rows={[
                ["Device", `Device #${snapshot.device?.device_id ?? deviceId} - ${installation.device_name || snapshot.device?.name || "-"}`],
                ["Model / type", [snapshot.device?.model_name, snapshot.device?.device_type].filter(Boolean).join(" / ")],
                ["Installation date", installation.date],
                ["Completed", fmtDateTime(installation.completed_at || snapshot.captured_at)],
                ["Installer", installation.installer_name || (installation.installer_user_id ? `User #${installation.installer_user_id}` : "-")],
                [
                  "Assignment",
                  `Assigned: Device #${installation.assignment?.device_id ?? deviceId} -> ${entity.display_name || `Customer #${installation.assignment?.entity_id}`} -> ${address.formatted_address || `Address #${installation.assignment?.address_id}`} -> ${asset.name || "Asset"} #${installation.assignment?.entity_address_item_id ?? "-"}${asset.map_identifier ? ` (${asset.map_identifier})` : ""}`,
                ],
                ["Subscription", subscription.subscription_id ? `#${subscription.subscription_id}${subscription.plan_id ? `, plan ${subscription.plan_id}` : ""}` : "Not returned"],
                ["Term anchor", subscription.start_date ? `Installation date ${subscription.start_date}` : "-"],
                [
                  "Included term",
                  policy.included_term_months != null
                    ? `${policy.included_term_months} month(s)${subscription.included_until ? `, through ${subscription.included_until}` : ""}`
                    : "-",
                ],
                ["Billing starts", subscription.billing_start_date],
                [
                  "Billing responsibility",
                  subscription.billing_owner_type === "entity"
                    ? `Moves to the customer (${entity.display_name || `Customer #${subscription.billing_owner_id}`}); handoff ${subscription.billing_state || "pending"}`
                    : "-",
                ],
              ]}
            />
          </Section>

          <Section title="Thresholds">
            <Grid
              head={["Threshold", "Comparison", "Value", "Alert", "Event"]}
              rows={(snapshot.thresholds || [])
                .filter((row) => String(row.threshold_value ?? "").trim() !== "")
                .map((row) => [
                  [row.parameter_label || `Parameter ${row.parameter_id}`, row.threshold_type].filter(Boolean).join(" - "),
                  row.comparison_label || row.comparison_operator,
                  `${row.threshold_value}${row.parameter_format ? ` ${row.parameter_format}` : ""}`,
                  row.alert_enabled ? "Enabled" : "Disabled",
                  row.event_enabled ? "Enabled" : "Disabled",
                ])}
              empty="No thresholds were configured."
            />
          </Section>

          {opsOf("level_calibration").length > 0 && (
            <Section title="Sensor calibration">
              <Typography variant="caption" display="block" mb={1}>
                Reviewed measurements from the installation. A saved value is shown only once the server
                confirms the calibration operation.
              </Typography>
              {opsOf("level_calibration").map((row) => (
                <Box key={row.operation_id} mb={1}>
                  <Grid
                    head={["Measurement", "Reviewed", "Saved"]}
                    rows={CALIBRATION_FIELDS.map(([field, label, unit]) => [
                      label,
                      calibrationValue(field, unit, row.payload?.[field]),
                      row.status === "saved"
                        ? calibrationValue(field, unit, row.result?.[field] ?? row.payload?.[field])
                        : "Not saved",
                    ])}
                    empty=""
                  />
                  <Typography variant="body2" mt={0.5}>
                    Outcome: {statusText(row)}
                    {row.actor_name || row.actor_user_id ? ` - by ${row.actor_name || `User #${row.actor_user_id}`}` : ""}
                    {lastAttempt(row) ? ` - last attempt ${fmtDateTime(lastAttempt(row))}` : ""}
                  </Typography>
                </Box>
              ))}
            </Section>
          )}

          <Section title="Alerts">
            <Typography variant="body2" mb={1}>
              {opsOf("notification_rule").length ? "Alerts on" : "Alerts off - no notification rules were requested at installation."}
            </Typography>
            <Grid
              head={["Recipient", "Method", "Alert", "Outcome"]}
              rows={opsOf("notification_rule").map((row) => [
                `${contactName(contacts, row.result?.contact_id || row.payload?.recipient?.contact_id)} - ${row.payload?.recipient?.destination_ref?.value || ""}`,
                labels.event_types?.[row.payload?.event_type_id] || row.payload?.recipient?.destination_ref?.kind,
                [labels.alert_types?.[row.payload?.alert_type_id], labels.alert_sub_types?.[row.payload?.alert_sub_type_id]].filter(Boolean).join(" / "),
                statusText(row),
              ])}
              empty="Not selected"
            />
          </Section>

          <Section title="Provider sharing">
            <Typography variant="caption" display="block" mb={1}>
              Sharing applies to the installed asset (the device&apos;s assigned tank / site).
            </Typography>
            <Grid
              head={["Provider", "Outcome"]}
              rows={opsOf("provider_share").map((row) => [
                labels.providers?.[row.payload?.provider_id] || `Provider #${row.payload?.provider_id}`,
                row.status === "saved" ? "Shared" : row.status === "failed" ? "Not shared - retry needed" : "Pending",
              ])}
              empty="Not selected"
            />
          </Section>

          <Section title="Contact device access">
            <Grid
              head={["Contact", "Contact ID", "Access Type", "Service Access Type", "Outcome"]}
              rows={opsOf("contact_access").map((row) => {
                const id = row.result?.contact_id || row.payload?.contact_ref?.contact_id;
                return [
                  contactName(contacts, id, row.payload?.contact_ref?.email),
                  id,
                  ACCESS_LABEL[row.payload?.access_type],
                  ACCESS_LABEL[row.payload?.service_access_type],
                  statusText(row),
                ];
              })}
              empty="Not granted"
            />
          </Section>

          <Section title="Attestation">
            <KV
              rows={[
                ["Information accurate", attestation.attest_data_accuracy ? "Confirmed" : "-"],
                ["Installation terms", attestation.accept_terms ? `Accepted (version ${attestation.terms_version || "-"})` : "-"],
                ["Accepted", fmtDateTime(attestation.accepted_at)],
              ]}
            />
          </Section>
        </>
      )}

      <Section title="Setup outcomes">
        <Grid
          head={["Operation", "Status", "Attempts", "Last attempt"]}
          rows={[
            ["Core installation", "Complete", "1", fmtDateTime(installation.completed_at || snapshot.captured_at || snapshot.install_completed_at)],
            // Calibration only appears when it was part of the reviewed plan, so
            // non-level devices do not show a misleading "Not selected" row.
            ...["provider_share", "contact_access", "level_calibration", "notification_rule"]
              .filter((kind) => kind !== "level_calibration" || opsOf(kind).length > 0)
              .map((kind) => {
              const rows = opsOf(kind);
              const name = OPERATION_KIND_LABEL[kind];
              if (!rows.length) return [name, "Not selected", "-", "-"];
              const status = setupState(rows) === "complete" ? "Complete" : setupState(rows) === "needs_attention" ? "Needs retry" : "Pending";
              const attempts = rows.reduce((sum, row) => sum + Number(row.attempt || row.attempts?.length || 0), 0);
              const last = rows.map(lastAttempt).filter(Boolean).sort().pop();
              return [`${name} (${rows.length})`, status, String(attempts), fmtDateTime(last)];
            }),
          ]}
          empty=""
        />
        {operations.filter((row) => row.status === "failed").map((row) => (
          <Typography key={row.operation_id} variant="caption" display="block" color="error">
            {OPERATION_KIND_LABEL[row.kind] || row.kind.replace("_", " ")}: {row.error?.message || row.error?.code || "Failed"}
          </Typography>
        ))}
      </Section>
    </Box>
  );
}
